export interface PaymentMethod {
  id: string;
  name: string;
  enabled: boolean;
}

// Precio unitario mínimo (fijo) para poder aplicar un descuento extra por artículo en el POS.
export const MIN_DISCOUNT_UNIT_PRICE = 1000;

export type DiscountRole = 'admin' | 'admin_cashier';
export type PrintFormat = '58mm' | '80mm' | 'A4';
export type SaleStatus = 'completed' | 'pending' | 'cancelled';

export interface SalesSettings {
  id: string;
  currency_code: string;
  currency_symbol: string;
  currency_name: string;
  taxes_enabled: boolean;
  tax_percentage: number;
  discounts_enabled: boolean;
  discount_percentage_allowed: boolean;
  discount_fixed_allowed: boolean;
  discount_max_percentage: number;
  discount_allowed_roles: DiscountRole;
  payment_methods: PaymentMethod[];
  cash_require_received: boolean;
  invoice_prefix: string;
  invoice_start_number: number;
  invoice_digits: number;
  invoice_last_number: number;
  sale_initial_status: SaleStatus;
  rounding_enabled: boolean;
  allow_sell_without_stock: boolean;
  allow_sell_inactive: boolean;
  require_sale_confirmation: boolean;
  auto_print_invoice: boolean;
  print_format: PrintFormat;
  default_printer: string;
  invoice_show_logo: boolean;
  invoice_show_address: boolean;
  invoice_show_phone: boolean;
  invoice_show_rnc: boolean;
  invoice_show_cashier: boolean;
  invoice_show_datetime: boolean;
  invoice_show_payment_method: boolean;
  invoice_footer_message: string;
  created_at?: string;
  updated_at?: string;
}

export const DEFAULT_PAYMENT_METHODS: PaymentMethod[] = [
  { id: 'cash', name: 'Efectivo', enabled: true },
  { id: 'card', name: 'Tarjeta', enabled: true },
  { id: 'transfer', name: 'Transferencia', enabled: true },
  { id: 'mixed', name: 'Pago mixto', enabled: true },
];

// Compatibilidad: las configuraciones antiguas guardaban "Otro" (id "other").
// Se migra automáticamente a "Pago mixto" sin perder la preferencia de activado.
const LEGACY_PAYMENT_IDS: Record<string, string> = { other: 'mixed' };

export function normalizePaymentMethods(
  methods: PaymentMethod[] | null | undefined
): PaymentMethod[] {
  const list = Array.isArray(methods) && methods.length > 0 ? methods : DEFAULT_PAYMENT_METHODS;
  const seen = new Set<string>();
  const result: PaymentMethod[] = [];

  list.forEach((m) => {
    if (!m || typeof m.id !== 'string') return;
    const id = LEGACY_PAYMENT_IDS[m.id] ?? m.id;
    if (seen.has(id)) return;
    seen.add(id);
    result.push({
      id,
      name: id === 'mixed' ? 'Pago mixto' : m.name,
      enabled: m.enabled !== false,
    });
  });

  if (!seen.has('mixed')) {
    result.push({ id: 'mixed', name: 'Pago mixto', enabled: true });
  }

  return result;
}

export const DEFAULT_SETTINGS: SalesSettings = {
  id: '00000000-0000-0000-0000-000000000001',
  currency_code: 'DOP',
  currency_symbol: 'RD$',
  currency_name: 'Peso dominicano',
  taxes_enabled: false,
  tax_percentage: 18,
  discounts_enabled: false,
  discount_percentage_allowed: true,
  discount_fixed_allowed: true,
  discount_max_percentage: 0,
  discount_allowed_roles: 'admin',
  payment_methods: DEFAULT_PAYMENT_METHODS,
  cash_require_received: true,
  invoice_prefix: 'FAC',
  invoice_start_number: 1,
  invoice_digits: 6,
  invoice_last_number: 0,
  sale_initial_status: 'completed',
  rounding_enabled: false,
  allow_sell_without_stock: false,
  allow_sell_inactive: false,
  require_sale_confirmation: true,
  auto_print_invoice: true,
  print_format: '80mm',
  default_printer: 'Sin impresora configurada',
  invoice_show_logo: true,
  invoice_show_address: true,
  invoice_show_phone: true,
  invoice_show_rnc: true,
  invoice_show_cashier: true,
  invoice_show_datetime: true,
  invoice_show_payment_method: true,
  invoice_footer_message: 'Gracias por su compra.',
};

// Etiquetas legibles para la auditoría
export const SETTING_LABELS: Record<string, string> = {
  currency_code: 'Código de moneda',
  currency_symbol: 'Símbolo de moneda',
  currency_name: 'Nombre de moneda',
  taxes_enabled: 'Aplicar impuestos',
  tax_percentage: 'Porcentaje de impuesto',
  discounts_enabled: 'Permitir descuentos',
  discount_percentage_allowed: 'Descuento por porcentaje',
  discount_fixed_allowed: 'Descuento por monto fijo',
  discount_max_percentage: 'Porcentaje máximo sin autorización',
  discount_allowed_roles: 'Quién puede aplicar descuentos',
  payment_methods: 'Métodos de pago',
  cash_require_received: 'Solicitar monto recibido',
  invoice_prefix: 'Prefijo de factura',
  invoice_start_number: 'Número inicial de factura',
  invoice_digits: 'Cantidad de dígitos',
  invoice_last_number: 'Último número de factura',
  sale_initial_status: 'Estado inicial de venta',
  rounding_enabled: 'Utilizar redondeo',
  allow_sell_without_stock: 'Vender sin existencia',
  allow_sell_inactive: 'Vender productos desactivados',
  require_sale_confirmation: 'Confirmación de venta',
  auto_print_invoice: 'Imprimir automáticamente',
  print_format: 'Formato de impresión',
  default_printer: 'Impresora predeterminada',
  invoice_show_logo: 'Mostrar logo',
  invoice_show_address: 'Mostrar dirección',
  invoice_show_phone: 'Mostrar teléfono',
  invoice_show_rnc: 'Mostrar RNC',
  invoice_show_cashier: 'Mostrar cajero',
  invoice_show_datetime: 'Mostrar fecha y hora',
  invoice_show_payment_method: 'Mostrar método de pago',
  invoice_footer_message: 'Mensaje final de factura',
};

// Convierte un valor a texto legible para la auditoría
export function formatSettingValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Sí' : 'No';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((m) => `${m.name}: ${m.enabled ? 'Sí' : 'No'}`)
      .join(', ');
  }
  if (key === 'discount_allowed_roles') {
    return value === 'admin' ? 'Administrador' : 'Administrador y cajero';
  }
  if (key === 'sale_initial_status') {
    const map: Record<string, string> = {
      completed: 'Completada',
      pending: 'Pendiente',
      cancelled: 'Anulada',
    };
    return map[String(value)] ?? String(value);
  }
  return String(value);
}

// Genera una vista previa del número de factura
export function formatInvoiceNumber(settings: SalesSettings): string {
  const next = settings.invoice_last_number + 1;
  const raw = String(settings.invoice_start_number + next - 1).padStart(
    settings.invoice_digits,
    '0'
  );
  const prefix = settings.invoice_prefix.trim();
  return prefix ? `${prefix}-${raw}` : raw;
}

// Formatea un monto según la moneda configurada
export function formatMoney(settings: SalesSettings, amount: number): string {
  const symbol = settings.currency_symbol || 'RD$';
  return `${symbol} ${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}